import { Network } from '@mogul/api-client';
import {
  ContractAddress,
  ContractAddressBinance,
  ContractAddressBinanceTest,
  ContractAddressGoerli,
  ContractAddressRinkeby,
  NATIVE_CURRENCY_ADDRESS,
} from '@mogul/shared';
import type { BigNumber, BigNumberish, Contract } from 'ethers';
import { ethers, providers } from 'ethers';
import {
  BrowserProvider,
  FallbackProvider,
  JsonRpcProvider,
  JsonRpcSigner,
} from 'ethers-v6';
import type { HttpTransport } from 'viem';
import { watchAsset } from 'viem/actions';
import type { PublicClient, WalletClient } from 'wagmi';
import { getWalletClient } from 'wagmi/actions';

import {
  CHAIN_ID_TO_KEY_MAP,
  CHAIN_ID_TO_TYPE_MAP,
  CURRENT_CHAIN_IDS_MAP,
  WEB3_CONFIG,
} from './wagmi';

// starting block height for staking - probably not needed
export const BLOCK_HEIGHT_MINIMUM_STAKING = 12292400;

export const POOL_IDS: { [key: string]: { [subKey: string]: number } } = {
  MAINNET: { STARS: 0, STARS_ETH: 1 },
  RINKEBY: { STARS: 0, STARS_ETH: 1 },
};
export const STARS_ADDRESS: { [key: string]: string } = {
  MAINNET: ContractAddress.Stars,
  RINKEBY: ContractAddressRinkeby.Stars,
  GOERLI: ContractAddressGoerli.Stars,
  BSCTEST: ContractAddressBinanceTest.Stars,
  BINANCE: ContractAddressBinance.Stars,
};

export const STARS_ETH_CONTRACT_ADDRESS: { [key: string]: string } = {
  MAINNET: ContractAddress.StarsEth,
  RINKEBY: ContractAddressRinkeby.StarsEth,
  GOERLI: ContractAddressGoerli.StarsEth,
  BINANCE: ContractAddressBinance.StarsBnb,
  BSCTEST: ContractAddressBinanceTest.StarsBnb,
};

export const STARS_MASTER_CHEF_ADDRESS: { [key: string]: string } = {
  MAINNET: ContractAddress.StarsMasterChef,
  RINKEBY: ContractAddressRinkeby.StarsMasterChef,
  GOERLI: ContractAddressGoerli.StarsMasterChef,
};

export const ACCESS_PASS_CONTRACT_ADDRESS: { [key: string]: string } = {
  MAINNET: ContractAddress.AccessPassSale,
  RINKEBY: ContractAddressRinkeby.AccessPassSale,
  GOERLI: ContractAddressGoerli.AccessPassSale,
};

export const USDC_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.Usdc,
  RINKEBY: ContractAddressRinkeby.Usdc,
  BSCTEST: ContractAddressBinanceTest.Usdc,
  BINANCE: ContractAddressBinance.Usdc,
  GOERLI: ContractAddressGoerli.StarsEth,
};

export const USDT_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.Usdt,
  BSCTEST: ContractAddressBinanceTest.Usdt,
  BINANCE: ContractAddressBinance.Usdt,
};

export const TOKEN_TO_ADDRESS: Record<string, Record<string, string>> = {
  USDC: USDC_ADDRESS,
  USDT: USDT_ADDRESS,
  STARS: STARS_ADDRESS,
};

export const AUCTION_HOUSE_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.AuctionHouse,
  RINKEBY: ContractAddressRinkeby.AuctionHouse,
  GOERLI: ContractAddressGoerli.AuctionHouse,
};

export const MARKETPLACE_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulMarketplace,
  RINKEBY: ContractAddressRinkeby.MogulMarketplace,
  GOERLI: ContractAddressGoerli.MogulMarketplace,
  BSCTEST: ContractAddressBinanceTest.MogulMarketplace,
  BINANCE: ContractAddressBinance.MogulMarketplace,
};

export const MARKETPLACE_ADDRESS_ERC_721: Record<string, string> = {
  MAINNET: ContractAddress.MogulMarketplaceErc721,
  RINKEBY: ContractAddressRinkeby.MogulMarketplaceErc721,
  GOERLI: ContractAddressGoerli.MogulMarketplaceErc721,
  BSCTEST: ContractAddressBinanceTest.MogulMarketplaceErc721,
  BINANCE: ContractAddressBinance.MogulMarketplaceErc721,
};

export const NFT_CONTRACT_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulNft,
  RINKEBY: ContractAddressRinkeby.MogulNft,
  GOERLI: ContractAddressGoerli.MogulNft,
  BSCTEST: ContractAddressBinanceTest.MogulNft,
  BINANCE: ContractAddressBinance.MogulNft,
};
//
export const ERC1155MINTABLE_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulNft,
  RINKEBY: ContractAddressRinkeby.MogulNft,
  GOERLI: ContractAddressGoerli.MogulNft,
  BSCTEST: ContractAddressBinanceTest.MogulNft,
  BINANCE: ContractAddressBinance.MogulNft,
};

export const NFT_ERC_721_CONTRACT_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulNftErc721,
  RINKEBY: ContractAddressRinkeby.MogulNftErc721,
  GOERLI: ContractAddressGoerli.MogulNftErc721,
  BSCTEST: ContractAddressBinanceTest.MogulNftErc721,
  BINANCE: ContractAddressBinance.MogulNftErc721,
};

export const MOVIE_VOTING_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulMovieVoting,
  RINKEBY: ContractAddressRinkeby.MogulMovieVoting,
  GOERLI: ContractAddressGoerli.MogulMovieVoting,
  BSCTEST: ContractAddressBinanceTest.MogulMovieVoting,
  BINANCE: ContractAddressBinance.MogulMovieVoting,
};

export const MOVIE_CHEF_VOTING_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulMovieMasterChef,
  RINKEBY: ContractAddressRinkeby.MogulMovieMasterChef,
  GOERLI: ContractAddressGoerli.MogulMovieMasterChef,
  BSCTEST: ContractAddressBinanceTest.MogulMovieMasterChef,
  BINANCE: ContractAddressBinance.MogulMovieMasterChef,
};

export const MOVIE_VOTING_NFT_ADDRESS: Record<string, string> = {
  MAINNET: ContractAddress.MogulMovieNft,
  RINKEBY: ContractAddressRinkeby.MogulMovieNft,
  GOERLI: ContractAddressGoerli.MogulMovieNft,
  BSCTEST: ContractAddressBinanceTest.MogulMovieNft,
  BINANCE: ContractAddressBinance.MogulMovieNft,
};

export const Jancroon_Contract_Address: Record<string, string> = {
  MAINNET: ContractAddress.Jancroon,
  RINKEBY: ContractAddressRinkeby.Jancroon,
};

export const STARS_STAKING_Address: Record<string, string> = {
  RINKEBY: ContractAddressRinkeby.StarsStaking,
  GOERLI: ContractAddressGoerli.StarsStaking,
  BINANCE: ContractAddressBinance.StarsStaking,
  BSCTEST: ContractAddressBinanceTest.StarsStaking,
  MAINNET: ContractAddress.StarsStaking,
};

export const NETWORK_CURRENCY_SYMBOL: Record<string, string> = {
  [Network.Goerli]: 'gETH',
  [Network.Mainnet]: 'ETH',
  [Network.Bsctest]: 'tBNB',
  [Network.Binance]: 'BNB',
};

export const formatEther = (value: BigNumber) =>
  parseFloat(ethers.utils.formatEther(value));

export const parseEther = (value: string) => ethers.utils.parseEther(value);

export const weiToEth = (valueMantissa: BigNumberish) => {
  const value = ethers.utils.formatEther(valueMantissa);
  const [whole, decimals] = value.split('.');
  return whole + (decimals === '0' ? '' : '.' + decimals);
};

export const ethToWei = (value: string) => ethers.utils.parseEther(value);

export const publicClientToProvider = ({ chain, transport }: PublicClient) => {
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  if (transport.type !== 'fallback') {
    return new providers.JsonRpcProvider(transport.url, network);
  }

  return new providers.FallbackProvider(
    (transport.transports as ReturnType<HttpTransport>[]).map(
      ({ value }) => new providers.JsonRpcProvider(value?.url, network),
    ),
  );
};

export const publicClientToProviderV6 = ({
  chain,
  transport,
}: PublicClient) => {
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  if (transport.type !== 'fallback') {
    return new JsonRpcProvider(transport.url, network);
  }

  const _providers = (transport.transports as ReturnType<HttpTransport>[]).map(
    ({ value }) => new JsonRpcProvider(value?.url, network),
  );

  if (_providers.length === 1) {
    return _providers[0];
  }

  return new FallbackProvider(_providers);
};

export const walletClientToSignerV6 = ({
  account,
  chain,
  transport,
}: WalletClient) => {
  const network = {
    chainId: chain.id,
    name: chain.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  const provider = new BrowserProvider(transport, network);
  const signer = new JsonRpcSigner(provider, account.address);

  return signer;
};

export const walletClientToSigner = ({
  chain,
  transport,
  account,
}: WalletClient) => {
  const network = {
    chainId: chain?.id,
    name: chain?.name,
    ensAddress: chain.contracts?.ensRegistry?.address,
  };

  const provider = new providers.Web3Provider(transport, network);
  const signer = provider.getSigner(account.address);

  return signer;
};

export const getWalletClientSafe = async (
  ...args: Parameters<typeof getWalletClient>
) => {
  const walletClient = await getWalletClient(...args);

  if (!walletClient) {
    throw Error('Expected to have wallet client');
  }

  return walletClient;
};

export const addStarsTokenToWallet = async (
  chain: keyof typeof WEB3_CONFIG.chains,
) => {
  const walletClient = await getWalletClientSafe();

  return watchAsset(walletClient, {
    type: 'ERC20',
    options: {
      address: WEB3_CONFIG.contracts[chain].stars,
      symbol: 'MOGUL',
      decimals: 18,
      image: 'https://etherscan.io/token/images/mogulstars_32.png?=v1',
    },
  });
};

// Generic Contract Utils

export const approveContract = async (
  contract: Contract,
  spender: string,
  amount: BigNumber,
): Promise<boolean> => {
  try {
    const didApprove = await contract.approve(spender, amount);
    await didApprove.wait();

    return didApprove;
  } catch (e) {
    window.Sentry?.captureException(e, {
      tags: { file: 'smart contracts utils', function: 'approveContract' },
      contexts: { data: { spender } },
    });
    console.error('contract approval error', e);
    return false;
  }
};

export const getContractAllowance = async (
  contract: Contract,
  owner: string,
  spender: string,
): Promise<number> => {
  try {
    const allowance: BigNumber = await contract.allowance(owner, spender);
    const allowanceNumber = parseFloat(ethers.utils.formatEther(allowance));
    return allowanceNumber;
  } catch (e) {
    window.Sentry?.captureException(e, {
      tags: { file: 'smart contracts utils', function: 'getContractAllowance' },
      contexts: { data: { spender, owner, contract } },
    });
    console.error(e);
    return 0;
  }
};

export const isEqualAddresses = (address1: string, address2: string) => {
  return address1?.toLowerCase?.() === address2?.toLowerCase?.();
};

export const getCurrentChainId = () => {
  const cachedChainId = sessionStorage.getItem(
    process.env.REACT_APP_SS_CHAIN_ID_KEY!,
  );

  const isSupportedChain = Number(cachedChainId) in CURRENT_CHAIN_IDS_MAP;
  const targetChainId = isSupportedChain
    ? Number(cachedChainId)
    : WEB3_CONFIG.chains.bsc.id;

  return targetChainId;
};

export const getCurrentChainKey = () => {
  return CHAIN_ID_TO_KEY_MAP[getCurrentChainId()];
};

export const getCurrentChainType = () => {
  return CHAIN_ID_TO_TYPE_MAP[getCurrentChainId()];
};

export const getNetworkCurrencySymbol = () => {
  const chainId = getCurrentChainId();
  const chainKey = CHAIN_ID_TO_KEY_MAP[chainId];

  return WEB3_CONFIG.chains[chainKey]?.nativeCurrency?.symbol;
};

export const getStarsAddress = () => {
  const chainId = getCurrentChainId();
  const chainKey = CHAIN_ID_TO_KEY_MAP[chainId];

  return WEB3_CONFIG.contracts[chainKey].stars;
};

export const getStarsListingTokenIndex = (listingTokens: string[]) => {
  const starsAddress = getStarsAddress();
  const starsListingTokenIndex = listingTokens.findIndex(listingToken =>
    isEqualAddresses(listingToken, starsAddress),
  );

  return starsListingTokenIndex;
};

export const getStarsListingTokenPrice = (
  listingTokens: string[],
  listingPrices: BigNumber[],
) => {
  const starsListingTokenIndex = getStarsListingTokenIndex(listingTokens);
  const starsListingTokenPrice = listingPrices[starsListingTokenIndex];
  return starsListingTokenPrice;
};

export const getEthListingTokenIndex = (listingTokens: string[]) => {
  const ethListingTokenIndex = listingTokens.findIndex(listingToken =>
    isEqualAddresses(listingToken, NATIVE_CURRENCY_ADDRESS),
  );
  // const ethListingTokenPrice = listingPrices[ethListingTokenIndex];

  return ethListingTokenIndex;
};

export const getEthListingTokenPrice = (
  listingTokens: string[],
  listingPrices: BigNumber[],
) => {
  const ethListingTokenIndex = getEthListingTokenIndex(listingTokens);
  const ethListingTokenPrice = listingPrices[ethListingTokenIndex];
  return ethListingTokenPrice;
};

export const getTokenSymbol = (tokenAddress: string) => {
  if (isEqualAddresses(tokenAddress, NATIVE_CURRENCY_ADDRESS)) {
    return getNetworkCurrencySymbol();
  } else if (isEqualAddresses(tokenAddress, getStarsAddress())) {
    return 'MOGUL';
  }
  return 'UNKNOWN';
};
